import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, User, X, Package } from 'lucide-react';
import { Button } from '../../ui/button';
import { formatPrice, formatDate } from '../utils';

const CLIENT_COLORS = [
  { bg: 'rgba(0,240,255,0.18)', border: '#00F0FF', text: '#67e8f9' },
  { bg: 'rgba(57,255,20,0.18)', border: '#39FF14', text: '#86efac' },
  { bg: 'rgba(255,92,0,0.18)', border: '#FF5C00', text: '#fdba74' },
  { bg: 'rgba(0,122,255,0.20)', border: '#007AFF', text: '#93c5fd' },
  { bg: 'rgba(236,72,153,0.18)', border: '#EC4899', text: '#f9a8d4' },
  { bg: 'rgba(168,85,247,0.20)', border: '#A855F7', text: '#d8b4fe' },
  { bg: 'rgba(250,204,21,0.18)', border: '#FACC15', text: '#fde047' },
  { bg: 'rgba(255,0,60,0.18)', border: '#FF003C', text: '#fca5a5' },
  { bg: 'rgba(45,212,191,0.18)', border: '#2DD4BF', text: '#5eead4' },
  { bg: 'rgba(244,114,182,0.18)', border: '#F472B6', text: '#f9a8d4' },
];

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MAX_LANES = 4;

const CalendarView = ({ calendarEvents = [], stock = [] }) => {
  const now = new Date();
  const [viewMode, setViewMode] = useState('month');
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [hoveredClientKey, setHoveredClientKey] = useState(null);
  const [detail, setDetail] = useState(null);

  // Assign a unique color per client
  const clientColorMap = useMemo(() => {
    const map = {};
    let idx = 0;
    calendarEvents.forEach(event => {
      const key = event.customer_name || event.customer_email || event.id;
      if (!map[key]) {
        map[key] = CLIENT_COLORS[idx % CLIENT_COLORS.length];
        idx++;
      }
    });
    return map;
  }, [calendarEvents]);

  const ACTIVE_STATUSES = ['confirmed', 'shipped', 'delivered', 'active'];
  const isActiveRental = (e) => (
    ACTIVE_STATUSES.includes(e.status)
    && e.subscription_status !== 'cancelled'
    && e.status !== 'terminated'
    && e.status !== 'cancelled'
  );

  // Build list of active rentals with display start/end (anniversary-aware)
  const rentalSpans = useMemo(() => {
    return calendarEvents
      .filter(isActiveRental)
      .map(event => {
        const anchorRaw = event.rental_start_date || event.delivered_at || event.start || event.created_at;
        const anchor = new Date(anchorRaw);
        const endRaw = event.cancelled_at || event.rental_end_date;
        const end = endRaw ? new Date(endRaw) : null;
        const clientKey = event.customer_name || event.customer_email || event.id;
        return { ...event, anchor, end, clientKey, color: clientColorMap[clientKey] || CLIENT_COLORS[0] };
      });
  }, [calendarEvents, clientColorMap]);

  // Is a rental active on a given day of the rendered month?
  const isRentalActiveOnDay = (r, year, month, day) => {
    const dayDate = new Date(year, month, day);
    const a = r.anchor;
    const globalStart = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    if (dayDate < globalStart) return false;
    const renderedMonthEnd = new Date(year, month + 1, 1);
    const ongoingEnd = renderedMonthEnd > globalStart ? renderedMonthEnd : new Date(a.getFullYear(), a.getMonth() + 1, a.getDate());
    const globalEnd = r.end ? r.end : ongoingEnd;
    if (dayDate >= globalEnd) return false;
    return true;
  };

  // Rentals active on a single day (used by the compact year view)
  const getRentalsForDay = (year, month, day) => rentalSpans.filter(r => isRentalActiveOnDay(r, year, month, day));

  // Split the month into week rows of 7 day-slots (null = outside month)
  const buildWeeks = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const adjusted = firstDay === 0 ? 6 : firstDay - 1;
    const weeks = [];
    for (let w = 0; w < 6; w++) {
      const days = [];
      for (let pos = 0; pos < 7; pos++) {
        const day = w * 7 + pos - adjusted + 1;
        days.push(day >= 1 && day <= daysInMonth ? day : null);
      }
      weeks.push(days);
    }
    return weeks.filter(week => week.some(d => d !== null));
  };

  // For a given week row, return each rental with its column span within the week
  const getWeekRentals = (year, month, weekDays) => {
    const list = [];
    rentalSpans.forEach(r => {
      let startCol = null, endCol = null;
      weekDays.forEach((day, pos) => {
        if (day === null) return;
        if (isRentalActiveOnDay(r, year, month, day)) {
          if (startCol === null) startCol = pos;
          endCol = pos;
        }
      });
      if (startCol !== null) list.push({ ...r, startCol, endCol });
    });
    list.sort((a, b) => (a.startCol - b.startCol) || String(a.clientKey).localeCompare(String(b.clientKey)));
    return list;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };
  const goToToday = () => {
    setViewMonth(now.getMonth());
    setViewYear(now.getFullYear());
    setViewMode('month');
  };

  const slug = (s) => String(s || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // ---------- Compact (year mini-month) ----------
  const renderCompactMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const adjusted = firstDay === 0 ? 6 : firstDay - 1;
    const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();
    return (
      <div>
        <h4 className="text-sm font-semibold text-white mb-2 text-center">{MONTH_NAMES[month]}</h4>
        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: 42 }, (_, i) => {
            const day = i - adjusted + 1;
            const isValid = day > 0 && day <= daysInMonth;
            const isToday = isValid && day === now.getDate() && isCurrentMonth;
            const activeRentals = isValid ? getRentalsForDay(year, month, day) : [];
            return (
              <div
                key={i}
                className={`h-6 flex items-center justify-center text-[10px] rounded relative ${
                  !isValid ? '' : isToday ? 'ring-1 ring-[#DFFF00] font-bold text-[#DFFF00]' : 'text-slate-600'
                }`}
                title={activeRentals.length > 0 ? activeRentals.map(r => r.customer_name).join(', ') : ''}
              >
                {isValid ? day : ''}
                {isValid && activeRentals.length > 0 && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex gap-px">
                    {activeRentals.slice(0, 3).map((s, idx) => (
                      <span key={idx} className="w-1 h-1 rounded-full" style={{ backgroundColor: s.color.border }} />
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- Full month (weekly-row bar overlay) ----------
  const renderMonth = (year, month) => {
    const weeks = buildWeeks(year, month);
    const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();

    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-slate-800 bg-slate-800/40">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-slate-400 py-2 uppercase tracking-wider">{d}</div>
          ))}
        </div>

        {weeks.map((weekDays, wi) => {
          const weekRentals = getWeekRentals(year, month, weekDays);
          const visible = weekRentals.slice(0, MAX_LANES);
          const overflow = weekRentals.length - visible.length;
          return (
            <div key={wi} className="border-b border-slate-800 last:border-b-0">
              {/* Date numbers */}
              <div className="grid grid-cols-7">
                {weekDays.map((day, pos) => {
                  const isToday = day && day === now.getDate() && isCurrentMonth;
                  return (
                    <div
                      key={pos}
                      className={`relative px-2 pt-2 ${day ? '' : 'bg-slate-900/40'} ${isToday ? 'bg-[#DFFF00]/[0.06]' : ''}`}
                      style={{ borderRight: pos < 6 ? '1px solid rgba(30,41,59,0.6)' : 'none' }}
                    >
                      {isToday && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#DFFF00]" />}
                      {day && (
                        <span className={`inline-flex items-center justify-center text-xs font-mono ${
                          isToday ? 'bg-[#DFFF00] text-black font-bold w-6 h-6 rounded-full' : 'text-slate-400'
                        }`}>{day}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rental bars (grid: column span = days, row = lane) */}
              <div className="grid grid-cols-7 gap-x-1 px-1 pb-2 pt-0.5" style={{ gridAutoRows: '26px', rowGap: '4px', minHeight: '38px' }}>
                {visible.map((r, lane) => {
                  const dim = hoveredClientKey && r.clientKey !== hoveredClientKey;
                  const hi = hoveredClientKey && r.clientKey === hoveredClientKey;
                  return (
                    <button
                      key={`${r.id}-${lane}`}
                      type="button"
                      onMouseEnter={() => setHoveredClientKey(r.clientKey)}
                      onMouseLeave={() => setHoveredClientKey(null)}
                      onClick={() => setDetail({ title: r.customer_name, rentals: [r] })}
                      data-testid={`bar-rental-${slug(r.customer_name)}`}
                      className={`flex items-center gap-1.5 h-[26px] rounded-md px-2 overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-px ${
                        dim ? 'opacity-20 grayscale' : ''
                      } ${hi ? 'ring-1 ring-white brightness-110' : ''}`}
                      style={{
                        gridColumn: `${r.startCol + 1} / ${r.endCol + 2}`,
                        gridRow: lane + 1,
                        backgroundColor: r.color.bg,
                        borderLeft: `3px solid ${r.color.border}`,
                      }}
                      title={`${r.customer_name} — ${r.pack_name}`}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color.border }} />
                      <span className="text-[11px] font-medium text-white truncate">{String(r.customer_name || '').split(' ')[0]}</span>
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={() => setDetail({ title: `${weekRentals.length} locations cette semaine`, rentals: weekRentals })}
                    data-testid="button-week-overflow"
                    className="flex items-center justify-center h-[26px] rounded-md text-[11px] font-mono text-slate-300 bg-slate-700/40 hover:bg-slate-700/80 hover:text-white transition-colors cursor-pointer"
                    style={{ gridColumn: '1 / 8', gridRow: MAX_LANES + 1 }}
                  >
                    +{overflow} autre{overflow > 1 ? 's' : ''} · voir tout
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#DFFF00]" />
          Calendrier des locations
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={viewMode === 'month' ? 'default' : 'ghost'}
            className={viewMode === 'month' ? 'bg-[#DFFF00] text-black hover:bg-[#c8e600]' : 'text-slate-400'}
            data-testid="button-view-month"
            onClick={() => setViewMode('month')}>Mois</Button>
          <Button size="sm" variant={viewMode === 'year' ? 'default' : 'ghost'}
            className={viewMode === 'year' ? 'bg-[#DFFF00] text-black hover:bg-[#c8e600]' : 'text-slate-400'}
            data-testid="button-view-year"
            onClick={() => setViewMode('year')}>Année</Button>
          <Button size="sm" variant="ghost" className="text-slate-400" data-testid="button-today" onClick={goToToday}>Aujourd'hui</Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4">
        <button onClick={() => { if (viewMode === 'year') setViewYear(viewYear - 1); else prevMonth(); }}
          data-testid="button-prev"
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold text-[#DFFF00] min-w-[200px] text-center">
          {viewMode === 'year' ? viewYear : `${MONTH_NAMES[viewMonth]} ${viewYear}`}
        </h3>
        <button onClick={() => { if (viewMode === 'year') setViewYear(viewYear + 1); else nextMonth(); }}
          data-testid="button-next"
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Pack summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stock.map((item) => {
          const packEvents = calendarEvents.filter(e => e.pack_id === item.pack_id && isActiveRental(e));
          return (
            <div key={item.pack_id} className="bg-slate-800 border-l-4 border-[#DFFF00] p-4 rounded-r-xl shadow-lg shadow-black/20">
              <p className="text-[11px] text-slate-400 uppercase tracking-wider truncate">{item.name}</p>
              <p className="text-2xl font-bold text-white font-mono mt-0.5">{packEvents.length}<span className="text-xs text-slate-400 font-sans font-normal ml-1.5">actif(s)</span></p>
            </div>
          );
        })}
      </div>

      {/* Helper hint */}
      {viewMode === 'month' && rentalSpans.length > 0 && (
        <p className="text-xs text-slate-500 text-center">
          Survolez une location pour suivre tout son parcours · cliquez pour les détails
        </p>
      )}

      {/* Calendar Grid */}
      {viewMode === 'month' ? renderMonth(viewYear, viewMonth) : (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i}
                className={`bg-slate-800/50 rounded-lg p-2 cursor-pointer hover:bg-slate-800 transition-colors ${
                  i === now.getMonth() && viewYear === now.getFullYear() ? 'ring-1 ring-[#DFFF00]' : ''
                }`}
                onClick={() => { setViewMonth(i); setViewMode('month'); }}>
                {renderCompactMonth(viewYear, i)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      {rentalSpans.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-800 p-4">
          <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" /> Légende clients
          </h4>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {Object.entries(clientColorMap).map(([clientName, color]) => (
              <button
                key={clientName}
                type="button"
                onMouseEnter={() => setHoveredClientKey(clientName)}
                onMouseLeave={() => setHoveredClientKey(null)}
                data-testid={`legend-${slug(clientName)}`}
                className={`flex items-center gap-2 text-xs font-mono transition-opacity ${
                  hoveredClientKey && hoveredClientKey !== clientName ? 'opacity-30' : 'opacity-100'
                }`}
              >
                <span className="w-3 h-3 rounded" style={{ backgroundColor: color.bg, border: `1px solid ${color.border}` }} />
                <span style={{ color: color.text }}>{clientName}</span>
              </button>
            ))}
            <div className="flex items-center gap-2 text-xs font-mono">
              <span className="w-3 h-3 rounded-full bg-[#DFFF00]" />
              <span className="text-slate-400">Aujourd'hui</span>
            </div>
          </div>
        </div>
      )}

      {/* Rentals list */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h3 className="font-semibold text-white mb-4">
          Locations actives ({rentalSpans.length})
        </h3>
        <div className="space-y-2">
          {rentalSpans.length === 0 ? (
            <p className="text-slate-500 text-sm">Aucune location active</p>
          ) : (
            rentalSpans.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: event.color.bg, borderLeft: `3px solid ${event.color.border}` }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 rounded" style={{ backgroundColor: event.color.border }} />
                  <div>
                    <p className="text-sm text-white font-medium">{event.customer_name}</p>
                    <p className="text-xs text-slate-400">{event.pack_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium font-mono" style={{ color: event.color.text }}>{formatPrice(event.monthly_price)}/mois</p>
                  <p className="text-xs text-slate-500">
                    Début : {formatDate(event.anchor)}
                    {event.billing_anniversary_shifted_to && (
                      <span className="text-amber-400 ml-1">
                        · Prélèvement le {new Date(event.billing_anniversary_shifted_to).getDate()} du mois
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Detail side panel */}
      {detail && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setDetail(null)} data-testid="detail-backdrop" />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-slate-800/95 backdrop-blur-xl border-l border-slate-700 z-50 p-6 overflow-y-auto" data-testid="rental-detail-panel">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{detail.title}</h3>
              <button onClick={() => setDetail(null)} data-testid="button-close-detail"
                className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              {detail.rentals.map((r) => (
                <div key={r.id} className="rounded-lg p-4" style={{ backgroundColor: r.color.bg, borderLeft: `3px solid ${r.color.border}` }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color.border }} />
                    <p className="text-white font-semibold">{r.customer_name}</p>
                  </div>
                  <p className="text-sm text-slate-300 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> {r.pack_name}</p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-slate-400">Début : {formatDate(r.anchor)}</span>
                    <span className="font-mono font-semibold" style={{ color: r.color.text }}>{formatPrice(r.monthly_price)}/mois</span>
                  </div>
                  {r.billing_anniversary_shifted_to && (
                    <p className="text-[11px] text-amber-400 mt-1.5">
                      Prélèvement le {new Date(r.billing_anniversary_shifted_to).getDate()} du mois
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarView;
